import { useState } from 'react'
import { useTexte } from '../../texte'

import { useSaveSettings, useSettings } from '../../hooks/queries'
import { knopfVariablen } from '../../lib/knopffarbe'
import { fehlertext } from '../../api/client'

/**
 * Die Farbe der Knöpfe — für die ganze Anwendung.
 *
 * **Chatübergreifend, und genau so gewünscht:** „Knöpfe wie ‚Freigeben',
 * ‚GPX importieren' etc — in den allgemeinen Einstellungen konfigurierbar."
 * Sie steht deshalb hier und nicht an einer Reise; eine Reise soll nicht
 * anders aussehen als die nächste.
 *
 * **Mit Vorschau**, wie bei den Sprechblasen. Eine Farbe, die man erst nach
 * dem Sichern sieht, wählt man blind — und diese hier färbt jeden Knopf der
 * Anwendung, das will man vorher wissen.
 *
 * **Automatisch ist kein Sonderfall, sondern der wichtige Zustand.** Leer
 * heißt: es gilt, was das Erscheinungsbild vorgibt, und die dunkle
 * Darstellung behält ihren eigenen, helleren Ton.
 */
export function KnopffarbeCard() {
  const t = useTexte()
  const settings = useSettings()
  const save = useSaveSettings()
  const [entwurf, setEntwurf] = useState<string | null>(null)
  const [gemeldet, setGemeldet] = useState(false)

  const daten = settings.data
  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat entschieden.
  if (!daten?.ceiling) return null

  const gespeichert = daten.settings.accent_color ?? ''
  const gilt = entwurf ?? gespeichert
  const geaendert = gilt !== gespeichert

  // Die Vorschau trägt die Variablen als eigene Angabe und nicht am
  // Wurzelelement: solange nicht gesichert ist, soll sich dieses Beispiel
  // ändern und nicht die halbe Anwendung.
  const probe = Object.fromEntries(
    Object.entries(knopfVariablen(gilt)).filter(([, wert]) => wert !== null),
  ) as Record<string, string>

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.farben.knoepfe}</h2>
      <p className="card__lead card__lead--absatz">{t.farben.knoepfeLead}</p>

      <div className="chatfarbe">
        {/* Ein natives Farbfeld: auf dem iPad öffnet das den Farbwähler des
            Systems, und der kann mehr, als ich hier je nachbauen sollte. */}
        <input
          type="color"
          className="kartenregler__farbe"
          name="accent_color"
          aria-label={t.farben.knoepfe}
          // Ein Farbfeld kennt kein „leer". Steht nichts, zeigt es den Ton
          // des hellen Erscheinungsbilds -- das ist die Farbe, die gerade
          // gilt, solange niemand am Gerät auf dunkel gestellt hat.
          value={gilt === '' ? '#007aff' : gilt}
          onChange={(event) => {
            setGemeldet(false)
            setEntwurf(event.target.value)
          }}
        />
        <span className="chatfarbe__text">
          <strong>{gilt === '' ? t.farben.automatisch : gilt}</strong>
          <span className="chatfarbe__hinweis">{t.farben.knoepfeAutomatisch}</span>
        </span>
        <button
          type="button"
          className="button button--outline button--small"
          disabled={gilt === ''}
          onClick={() => {
            setGemeldet(false)
            setEntwurf('')
          }}
        >
          {t.farben.automatisch}
        </button>
      </div>

      {/* Zwei echte Knöpfe, keine gemalten Kästchen: was hier steht, ist
          dieselbe Klasse wie auf der Karte und auf der Infoseite. */}
      <div className="knopfprobe" style={probe}>
        <button type="button" className="button" disabled>
          {t.farben.probeFreigeben}
        </button>
        <button type="button" className="button button--outline" disabled>
          {t.farben.probeImportieren}
        </button>
      </div>

      <div className="angabe angabe--weiter">
        <button
          type="button"
          className="button button--outline button--small"
          disabled={!geaendert || save.isPending}
          onClick={() =>
            save.mutate(
              { accent_color: gilt },
              {
                onSuccess: () => {
                  setEntwurf(null)
                  setGemeldet(true)
                },
              },
            )
          }
        >
          {save.isPending ? t.allgemein.laeuft : t.allgemein.speichern}
        </button>
        {gemeldet && !save.isPending && (
          <span className="winzig akzent">{t.allgemein.gespeichert}</span>
        )}
        {save.isError && (
          <span className="winzig" role="alert">
            {fehlertext(save.error)}
          </span>
        )}
      </div>
    </div>
  )
}
